import { BookingStartVisitDialog } from '@/components/organisms/bookings/BookingStartVisitDialog';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { format } from 'date-fns';
import { createFileRoute, useRouter, Link } from '@tanstack/react-router';
import {
  ArrowLeft,
  AlertCircle,
  Briefcase,
  RefreshCw,
  Paperclip,
  CheckSquare,
  Check,
  FileText,
  FileSignature,
  DollarSign,
  Clock,
  Loader2,
  Lock,
  StickyNote,
  CalendarPlus,
  ChevronDown,
  ChevronsUpDown,
  MapPin,
  Phone,
  PhoneCall,
  MessageSquare,
  Mail,
} from 'lucide-react';
import { Badge } from '@/components/atoms/badge';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { Skeleton } from '@/components/atoms/skeleton';
import { cn, isHtmlContent } from '@/lib/utils';
import { DEFAULT_DURATION_PRESETS } from '@/lib/scheduler/durationPresets';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/atoms/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/atoms/popover';
import { CollapsibleSectionCard } from '@/components/molecules/CollapsibleSectionCard';
import { RichTextContent } from '@/components/molecules';
import { BookingLinkedCall } from '@/components/calls/BookingLinkedCall';
import { DetailPageLayout, useWorkspaceHeader } from '@/components/layouts';
import { DEFAULT_ESTIMATE_LIST_FILTERS } from '@/components/organisms/estimates/support/estimate-list-filters';
import { useContactComms } from '@/lib/contacts/contactComms';
import { useBooking } from '@/hooks/bookings/useBooking';
import { useCancelBooking } from '@/hooks/bookings/useCancelBooking';
import { useUpdateBooking } from '@/hooks/bookings/useUpdateBooking';
import { useUpdateBookingStatus } from '@/hooks/bookings/useUpdateBookingStatus';
import { useContacts } from '@/hooks/contacts/useContacts';
import { useCreateJob } from '@/hooks/jobs/useCreateJob';
import { useJob } from '@/hooks/jobs/useJob';
import { useJobAppointment } from '@/hooks/jobs/useJobAppointments';
import { useJobs, useCreateServiceCallEstimate } from '@/hooks/jobs/useJobs';
import { useBookingAlternatives } from '@/hooks/scheduler/useBookingAlternatives';
import { useReserveSlot } from '@/hooks/scheduler/useReserveSlot';
import { useRescheduleBooking } from '@/hooks/scheduler/useRescheduleBooking';
import { useHasPermission, useIsFieldHidden } from '@/hooks/usePermissions';
import { JobEquipmentSection } from '@/components/organisms/jobs/JobEquipmentSection';
import { JobRelationshipsCard } from '@/components/organisms/jobs/JobRelationshipsCard';
import { EntityNotes } from '@/components/organisms/notes/EntityNotes';
import { BookingNextStepCard } from '@/components/organisms/bookings/BookingNextStepCard';
import { JobIrisSurveyCard } from '@/components/organisms/jobs/JobIrisSurveyCard';
import type { BookingQuickAction } from '@/components/organisms/bookings/support/booking-action-visibility';
import { AppointmentChecklists } from '@/components/organisms/jobs/AppointmentChecklists';
import { AppointmentFormSubmissions } from '@/components/organisms/jobs/AppointmentFormSubmissions';
import { VisitAside } from '@/components/organisms/jobs/VisitAside';
import { DEFAULT_INVOICE_LIST_FILTERS } from '@/components/organisms/invoices/InvoiceListPanel';
import {
  dateKeyInTimeZone,
  dateStringToDate,
  formatDateTimeInTimeZone,
  formatTimeZoneLabel,
} from '@/lib/date-values';
import { contactHasPhone } from '@/lib/contacts/phone';
import {
  SCHEDULE_MODE_LABELS,
  useScheduleTimezone,
  locationZoneOf,
} from '@/lib/time/scheduleTimezone';
import { ScheduleTimezoneToggle } from '@/components/molecules/ScheduleTimezoneToggle';
import type { Booking, BookingStatus } from '@/types/booking';
import type { Job } from '@/types/job';
import { buildJobFromBookingDto } from '@/lib/bookings/buildJobFromBookingDto';
import { stripHtml } from '@/lib/strip-html';
import type { JobAppointment, JobAppointmentStatus } from '@/types/jobAppointment';
import type { SchedulerAvailabilitySlot } from '@/types/scheduler';

export type BookingTab = 'overview' | 'notes';

export type ScheduledBookingTarget = {
  bookingId?: string;
  id?: string;
  appointmentId?: string | null;
  jobId?: string | null;
};

export const Route = createFileRoute('/_dashboard/dashboard/bookings/$bookingId')({
  component: BookingDetailPage,
});

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Search params the estimate detail route requires; mirrors the constant used
// elsewhere for estimate-detail links (NOT-246). Forwarding the booking page's
// own search instead would leave the <Link> with a valid href that doesn't
// navigate on click (TanStack v1.168).
const DEFAULT_ESTIMATE_DETAIL_SEARCH = {
  ...DEFAULT_ESTIMATE_LIST_FILTERS,
  tab: 'overview' as const,
};

const STATUS_VARIANT: Record<BookingStatus, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  scheduled: 'default',
  'in-progress': 'default',
  completed: 'secondary',
  cancelled: 'destructive',
  unscheduled: 'outline',
};

const STATUS_LABEL: Record<BookingStatus, string> = {
  scheduled: 'Scheduled',
  'in-progress': 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
  unscheduled: 'Unscheduled',
};

function isBookingLocked(status: BookingStatus) {
  return status === 'completed' || status === 'cancelled';
}

function lockedReasonForBooking(status: BookingStatus): 'completed' | 'cancelled' | 'readonly' {
  if (status === 'completed') return 'completed';
  if (status === 'cancelled') return 'cancelled';
  return 'readonly';
}

// Once intake is linked, show the backing Job's business identifier
// (JOB-000123) without relabeling this request-only context as another Job
// workspace. An unlinked request uses its immutable business reference; UUIDs
// are navigation keys, never presentation fallbacks.
function formatBookingTitle(booking: Booking, jobNumber?: string | null) {
  const service = booking.jobType?.name ?? booking.serviceName ?? booking.title ?? 'Service';
  return jobNumber
    ? `Request for ${jobNumber} · ${service}`
    : `${booking.requestNumber || 'Request unavailable'} · ${service}`;
}

function bookingAddressLabel(booking: Booking) {
  if (!booking.address) return null;
  return [booking.address.street, booking.address.city, booking.address.state, booking.address.zip]
    .filter(Boolean)
    .join(', ');
}

function technicianName(tech: NonNullable<Booking['technicians']>[number]) {
  return `${tech.firstName}${tech.lastName ? ' ' + tech.lastName : ''}`;
}

function bookingStatusToAppointmentStatus(status: BookingStatus): JobAppointmentStatus {
  if (status === 'completed') return 'COMPLETED';
  if (status === 'cancelled') return 'CANCELLED';
  if (status === 'in-progress') return 'IN_PROGRESS';
  if (status === 'unscheduled') return 'RESERVED';
  return 'SCHEDULED';
}

function bookingToAppointment(booking: Booking, mapAddress: string | null): JobAppointment {
  const technicians =
    booking.technicians?.map((tech, index) => ({
      id: tech.id,
      name: technicianName(tech),
      role: tech.role ?? (index === 0 ? ('LEAD' as const) : ('HELPER' as const)),
      email: tech.email,
      phone: tech.phone,
    })) ?? [];
  const primaryTechnician = technicians[0]
    ? { id: technicians[0].id, name: technicians[0].name }
    : null;

  return {
    id: booking.appointmentId ?? 'request-preview',
    status: bookingStatusToAppointmentStatus(booking.status),
    appointmentNumber: booking.appointmentNumber ?? null,
    startAt: booking.scheduledStart ?? null,
    endAt: booking.scheduledEnd ?? null,
    scheduledDate: booking.scheduledDate ?? null,
    granularity: booking.granularity ?? 'TIMED',
    timezone: booking.timezone ?? 'UTC',
    siteTimezone: booking.siteTimezone ?? null,
    serviceType: booking.serviceType
      ? {
          id: booking.serviceType.id,
          name: booking.serviceType.name,
          slug: booking.serviceType.slug ?? '',
        }
      : booking.serviceName
        ? { id: '', name: booking.serviceName, slug: '' }
        : null,
    primaryTechnician,
    technicians,
    notes: booking.internalNotes ?? null,
    // The issue the caller reported — NOT the job type name, which is what this
    // used to fall back to (it made every service call look like it had one).
    issueSummary: booking.issueSummary ?? null,
    customerNotes: booking.customerNotes ?? null,
    durationMinutes: booking.duration ?? null,
    serviceAddress: booking.address
      ? {
          street: booking.address.street ?? null,
          city: booking.address.city ?? null,
          state: booking.address.state ?? null,
          zip: booking.address.zip ?? null,
          country: booking.address.country ?? null,
          formatted: mapAddress,
        }
      : null,
    activityEvents: [
      {
        id: `${booking.id}:created`,
        eventType: 'BOOKING_CREATED',
        label: 'Request created',
        createdAt: booking.createdAt,
      },
      {
        id: `${booking.id}:updated`,
        eventType: 'BOOKING_UPDATED',
        label: 'Request updated',
        createdAt: booking.updatedAt,
      },
    ].filter((event) => event.createdAt),
  };
}

// ─── Page ─────────────────────────────────────────────────────────────────────

function BookingDetailPage() {
  const { bookingId } = Route.useParams();
  const router = useRouter();

  return (
    <BookingDetailContent
      bookingId={bookingId}
      onBack={() => void router.navigate({ to: '/dashboard/bookings' })}
    />
  );
}

interface BookingDetailContentProps {
  bookingId: string;
  tab?: BookingTab;
  onTabChange?: (tab: BookingTab) => void;
  onBack: () => void;
  allowScheduling?: boolean;
  onScheduled?: (updated?: ScheduledBookingTarget) => void;
}

// NOT-626 — bucket days in the SAME zone the rows render in (the schedule
// display zone), so a slot near midnight never sits under a day tab whose date
// disagrees with the time printed on the row.
function groupSlotsByDay(slots: SchedulerAvailabilitySlot[], displayZone: string) {
  const groups = new Map<string, SchedulerAvailabilitySlot[]>();
  for (const slot of slots) {
    const key = dateKeyInTimeZone(slot.startAt, displayZone) || slot.startAt.slice(0, 10);
    groups.set(key, [...(groups.get(key) ?? []), slot]);
  }
  return Array.from(groups.entries()).map(([day, items]) => ({ day, items }));
}

function ScheduleBookingDialog({
  bookingId,
  serviceTypeId,
  initialDurationMinutes,
  open,
  onOpenChange,
  onScheduled,
}: {
  bookingId: string;
  serviceTypeId?: string;
  initialDurationMinutes?: number | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onScheduled?: (updated?: ScheduledBookingTarget) => void;
}) {
  const [selectedSlot, setSelectedSlot] = useState<SchedulerAvailabilitySlot | null>(null);
  const [openDays, setOpenDays] = useState<Set<string>>(() => new Set());
  // Alternatives default to the booking's own length so an 8h booking offers
  // 8h slots (not 1h). The picker below lets the user pick a different length.
  const [durationMinutes, setDurationMinutes] = useState<number | null>(
    initialDurationMinutes ?? null,
  );
  // NOT-626 — the ONE display contract for every time in this dialog.
  const scheduleTz = useScheduleTimezone();
  const alternatives = useBookingAlternatives(bookingId, open, durationMinutes);
  const reserveSlot = useReserveSlot();
  const rescheduleBooking = useRescheduleBooking(bookingId);
  const slots = alternatives.data?.slots ?? [];
  const effectiveServiceTypeId = serviceTypeId ?? alternatives.data?.serviceTypeId;
  // Every alternative is for the SAME booking, hence one service address and
  // one site zone — resolve once. Bucketing on the session zone put a
  // near-midnight slot under a day tab whose label disagreed with the time
  // printed on the slot itself.
  const alternativesSiteZone = slots.find((slot) => slot.siteTimezone)?.siteTimezone ?? null;
  const groups = groupSlotsByDay(slots, scheduleTz.zoneFor(alternativesSiteZone));

  useEffect(() => {
    if (!open) {
      setSelectedSlot(null);
      setOpenDays(new Set());
      setDurationMinutes(initialDurationMinutes ?? null);
    }
  }, [open, initialDurationMinutes]);

  // Changing the requested length re-lists alternatives at that length and
  // clears any slot chosen against the previous length.
  function handleDurationChange(minutes: number) {
    setDurationMinutes(minutes);
    setSelectedSlot(null);
  }

  function toggleDay(day: string) {
    setOpenDays((current) => {
      const next = new Set(current);
      if (next.has(day)) {
        next.delete(day);
      } else {
        next.add(day);
      }
      return next;
    });
  }

  function handleSchedule() {
    if (!selectedSlot || !effectiveServiceTypeId) return;

    reserveSlot.mutate(
      {
        slotId: selectedSlot.slotId,
        technicianId: selectedSlot.technicianId,
        serviceTypeId: effectiveServiceTypeId,
        startAt: selectedSlot.startAt,
        endAt: selectedSlot.endAt,
        score: selectedSlot.score,
      },
      {
        onSuccess: (reserved) => {
          // Use the PERSISTED slot id from the reserve response — the engine may
          // have reused an existing slot for this technician+time, so the ephemeral
          // availability slotId would 404 on reschedule.
          rescheduleBooking.mutate(
            { newSlotId: reserved.id },
            {
              onSuccess: (updated) => {
                onOpenChange(false);
                onScheduled?.({
                  ...updated,
                  appointmentId: updated.appointmentId ?? null,
                });
              },
            },
          );
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Schedule Visit</DialogTitle>
          <DialogDescription>
            Pick an available slot to move this visit out of Schedule Review.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5">
          <Label>Duration</Label>
          <div className="flex flex-wrap items-center gap-2">
            {DEFAULT_DURATION_PRESETS.map((preset) => {
              const active = durationMinutes === preset.minutes;
              return (
                <button
                  key={`${preset.label}-${preset.minutes}`}
                  type="button"
                  className={cn(
                    'rounded-full border px-3 py-1 text-sm font-medium transition-colors',
                    active
                      ? 'border-blue-200 bg-blue-50 text-blue-700'
                      : 'border text-foreground hover:bg-muted',
                  )}
                  onClick={() => handleDurationChange(preset.minutes)}
                >
                  {preset.label}
                </button>
              );
            })}
          </div>
          {durationMinutes == null && (
            <p className="text-xs text-muted-foreground">Defaults to your slot length (60 min).</p>
          )}
        </div>

        <div className="max-h-[460px] overflow-y-auto pr-1">
          {alternatives.isLoading && (
            <div className="space-y-3">
              {Array.from({ length: 4 }).map((_, index) => (
                <Skeleton key={index} className="h-14 w-full rounded-lg" />
              ))}
            </div>
          )}

          {alternatives.isError && (
            <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed p-8 text-center text-muted-foreground">
              <AlertCircle size={24} />
              <p className="text-sm">Could not load available slots.</p>
              <Button variant="outline" size="sm" onClick={() => void alternatives.refetch()}>
                Retry
              </Button>
            </div>
          )}

          {!alternatives.isLoading && !alternatives.isError && groups.length === 0 && (
            <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed p-8 text-center text-muted-foreground">
              <CalendarPlus size={24} />
              <p className="text-sm">No available slots found for this visit.</p>
            </div>
          )}

          {groups.length > 0 && (
            <div className="space-y-4">
              {groups.map((group) => {
                const isOpen = openDays.has(group.day);
                const firstSlot = group.items[0];
                const dayLabel = firstSlot
                  ? scheduleTz.format(firstSlot.startAt, {
                      weekday: 'long',
                      month: 'short',
                      day: 'numeric',
                    })
                  : group.day;
                // Location-local zone for the "at location" hint: the geocoded
                // site zone when annotated, else the slot's company zone.
                const groupLocationZone = firstSlot
                  ? (locationZoneOf(firstSlot.siteTimezone, firstSlot.timezone) ?? null)
                  : null;
                return (
                  <div key={group.day} className="overflow-hidden rounded-lg border">
                    <button
                      type="button"
                      onClick={() => toggleDay(group.day)}
                      className="flex w-full items-center justify-between gap-3 bg-muted/50 px-3 py-2 text-left transition-colors hover:bg-muted"
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium">{dayLabel}</span>
                        <span className="block text-xs text-muted-foreground">
                          {group.items.length} available slots
                        </span>
                      </span>
                      <ChevronDown
                        size={16}
                        className={`shrink-0 text-muted-foreground transition-transform ${
                          isOpen ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    {isOpen && (
                      <div className="divide-y">
                        {group.items.map((slot) => {
                          const selected = selectedSlot?.slotId === slot.slotId;
                          return (
                            <button
                              key={slot.slotId}
                              type="button"
                              onClick={() => setSelectedSlot(slot)}
                              className={`flex w-full items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-muted/40 ${
                                selected ? 'bg-primary/5' : ''
                              }`}
                            >
                              <span
                                className={`flex size-4 shrink-0 items-center justify-center rounded-full border-2 ${
                                  selected
                                    ? 'border-primary bg-primary'
                                    : 'border-muted-foreground/30'
                                }`}
                              >
                                {selected && <Check size={9} className="text-primary-foreground" />}
                              </span>
                              <span className="min-w-0 flex-1">
                                <span className="block text-sm font-medium">
                                  {scheduleTz.format(
                                    slot.startAt,
                                    { hour: 'numeric', minute: '2-digit' },
                                    slot.siteTimezone,
                                  )}{' '}
                                  -{' '}
                                  {scheduleTz.format(
                                    slot.endAt,
                                    { hour: 'numeric', minute: '2-digit' },
                                    slot.siteTimezone,
                                  )}
                                </span>
                                {groupLocationZone &&
                                scheduleTz.differsFromDisplay(
                                  groupLocationZone,
                                  slot.siteTimezone,
                                ) ? (
                                  <span className="block truncate text-xs text-muted-foreground">
                                    {formatDateTimeInTimeZone(slot.startAt, groupLocationZone, {
                                      hour: 'numeric',
                                      minute: '2-digit',
                                    })}
                                    {' - '}
                                    {formatDateTimeInTimeZone(slot.endAt, groupLocationZone, {
                                      hour: 'numeric',
                                      minute: '2-digit',
                                    })}{' '}
                                    at location ·{' '}
                                    {formatTimeZoneLabel(groupLocationZone, new Date(slot.startAt))}
                                  </span>
                                ) : null}
                                <span className="block truncate text-xs text-muted-foreground">
                                  {slot.technicianName}
                                </span>
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSchedule}
            disabled={
              !selectedSlot ||
              !effectiveServiceTypeId ||
              reserveSlot.isPending ||
              rescheduleBooking.isPending
            }
          >
            {(reserveSlot.isPending || rescheduleBooking.isPending) && (
              <Loader2 size={14} className="mr-1.5 animate-spin" />
            )}
            Schedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function formatJobOption(job: Job) {
  return `${job.jobNumber} · ${job.name}`;
}

function jobMatchesBookingService(job: Job, booking: Booking) {
  if (booking.jobType?.id) return job.jobType.id === booking.jobType.id;
  const serviceTypeId = booking.serviceType?.id;
  const serviceTypeSlug = booking.serviceType?.slug;
  if (!serviceTypeId && !serviceTypeSlug) return true;

  return (job.jobType.requiredSkills ?? []).some(
    (skill) =>
      (serviceTypeId && skill.id === serviceTypeId) ||
      (serviceTypeSlug && skill.slug === serviceTypeSlug),
  );
}

function bookingContactName(booking: Booking) {
  if (!booking.contact) return null;
  return `${booking.contact.firstName}${booking.contact.lastName ? ' ' + booking.contact.lastName : ''}`;
}

function normalizeLookupText(value: string | null | undefined) {
  return (value ?? '').trim().toLowerCase();
}

function contactDisplayName(contact: {
  displayName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  companyName?: string | null;
}) {
  return (
    contact.displayName?.trim() ||
    `${contact.firstName ?? ''} ${contact.lastName ?? ''}`.trim() ||
    contact.companyName?.trim() ||
    ''
  );
}

function AttachJobDialog({
  booking,
  open,
  onOpenChange,
}: {
  booking: Booking;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [query, setQuery] = useState('');
  const [selectedJobId, setSelectedJobId] = useState('');
  const [selectedJobLabel, setSelectedJobLabel] = useState('');
  const [jobPickerOpen, setJobPickerOpen] = useState(false);
  const jobsQuery = useJobs(
    {
      q: query.trim() || undefined,
      limit: 100,
      sortBy: 'createdAt',
      sortDir: 'desc',
    },
    { enabled: open },
  );
  const updateBooking = useUpdateBooking();
  const createJob = useCreateJob();
  const existingContactId = booking.contactId || booking.contact?.id || '';
  const fallbackContactSearch = existingContactId
    ? ''
    : (bookingContactName(booking) ?? booking.contact?.phone ?? '');
  const fallbackContactsQuery = useContacts(
    {
      search: fallbackContactSearch,
      limit: 5,
    },
    {
      enabled: open && !existingContactId && fallbackContactSearch.trim().length >= 2,
    },
  );
  const jobs = useMemo(() => jobsQuery.data?.data ?? [], [jobsQuery.data?.data]);
  const compatibleJobs = useMemo(
    () => jobs.filter((job) => jobMatchesBookingService(job, booking)),
    [booking, jobs],
  );
  const fallbackContactId = useMemo(() => {
    if (existingContactId) return '';
    const contacts = fallbackContactsQuery.data?.contacts ?? [];
    if (contacts.length === 0) return '';

    const bookingName = normalizeLookupText(bookingContactName(booking));
    const matchedContact =
      contacts.find((contact) => contactHasPhone(contact, booking.contact?.phone)) ??
      contacts.find(
        (contact) =>
          bookingName && normalizeLookupText(contactDisplayName(contact)) === bookingName,
      ) ??
      contacts[0];

    return matchedContact?.id ?? '';
  }, [booking, existingContactId, fallbackContactsQuery.data?.contacts]);
  const requestServiceName =
    booking.jobType?.name ?? booking.serviceType?.name ?? booking.serviceName ?? 'this request';
  const effectiveContactId = existingContactId || fallbackContactId;
  const resolvingContact = !existingContactId && fallbackContactsQuery.isFetching;
  const canCreateJob = Boolean(effectiveContactId);

  useEffect(() => {
    if (open) return;
    setQuery('');
    setSelectedJobId('');
    setSelectedJobLabel('');
    setJobPickerOpen(false);
  }, [open]);

  useEffect(() => {
    if (!selectedJobId) return;
    if (compatibleJobs.some((job) => job.id === selectedJobId)) return;
    setSelectedJobId('');
    setSelectedJobLabel('');
  }, [compatibleJobs, selectedJobId]);

  function handleAttach() {
    if (!selectedJobId) return;
    updateBooking.mutate(
      { id: booking.id, jobId: selectedJobId },
      {
        onSuccess: () => onOpenChange(false),
      },
    );
  }

  function handleCreateJobFromBooking() {
    const dto = buildJobFromBookingDto(booking, effectiveContactId);
    if (!dto) return;

    createJob.mutate(dto, {
      onSuccess: () => onOpenChange(false),
    });
  }

  const isCreatingJob = createJob.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Attach to Job</DialogTitle>
          <DialogDescription>
            Link this request to an existing Job. It will no longer appear in the Requests queue.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Button
            type="button"
            variant="outline"
            className="h-auto w-full justify-start gap-3 px-3 py-3 text-left"
            disabled={
              !canCreateJob || resolvingContact || createJob.isPending || updateBooking.isPending
            }
            title={!canCreateJob ? 'Customer is still resolving for this request.' : undefined}
            onClick={handleCreateJobFromBooking}
          >
            {isCreatingJob || resolvingContact ? (
              <Loader2 className="size-4 shrink-0 animate-spin text-muted-foreground" />
            ) : (
              <Briefcase className="size-4 shrink-0 text-muted-foreground" />
            )}
            <span className="min-w-0">
              <span className="block font-medium">Create new Job from this request</span>
              <span className="block text-xs font-normal text-muted-foreground">
                Uses this customer, service, schedule, address, and tech.
              </span>
            </span>
          </Button>

          <Popover open={jobPickerOpen} onOpenChange={setJobPickerOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                className="h-10 w-full justify-between px-3 text-left font-normal"
                aria-expanded={jobPickerOpen}
              >
                <span className="truncate">
                  {selectedJobLabel ||
                    (jobsQuery.isFetching ? 'Loading jobs...' : 'Search existing jobs...')}
                </span>
                <ChevronsUpDown className="size-4 shrink-0 text-muted-foreground" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              className="w-[var(--radix-popover-trigger-width)] overflow-hidden p-0"
            >
              <div className="border-b p-2">
                <Input
                  autoFocus
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search jobs..."
                  className="h-9"
                />
              </div>
              <div
                className="max-h-72 overflow-y-auto overscroll-contain p-1"
                onWheel={(event) => event.stopPropagation()}
                onTouchMove={(event) => event.stopPropagation()}
              >
                {jobsQuery.isFetching ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="size-4 animate-spin text-muted-foreground" />
                  </div>
                ) : null}
                {!jobsQuery.isFetching && jobs.length === 0 ? (
                  <p className="py-6 text-center text-sm text-muted-foreground">
                    No matching jobs found.
                  </p>
                ) : null}
                {!jobsQuery.isFetching && jobs.length > 0 && compatibleJobs.length === 0 ? (
                  <p className="px-3 py-6 text-center text-sm text-muted-foreground">
                    No existing Jobs match {requestServiceName}. Create a new Job from this request
                    instead.
                  </p>
                ) : null}
                {!jobsQuery.isFetching && compatibleJobs.length > 0
                  ? compatibleJobs.map((job) => {
                      const label = formatJobOption(job);
                      const selected = selectedJobId === job.id;
                      return (
                        <button
                          key={job.id}
                          type="button"
                          className="flex w-full items-center gap-2 rounded-sm px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground"
                          onClick={() => {
                            setSelectedJobId(job.id);
                            setSelectedJobLabel(label);
                            setJobPickerOpen(false);
                          }}
                        >
                          <div className="min-w-0 flex-1">
                            <p className="truncate font-medium">{job.jobNumber}</p>
                            <p className="truncate text-xs text-muted-foreground">{job.name}</p>
                          </div>
                          {selected ? <Check className="size-4 text-primary" /> : null}
                        </button>
                      );
                    })
                  : null}
              </div>
            </PopoverContent>
          </Popover>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleAttach}
            disabled={!selectedJobId || updateBooking.isPending || createJob.isPending}
          >
            {updateBooking.isPending && <Loader2 size={14} className="mr-1.5 animate-spin" />}
            Attach
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DetailField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <div className="mt-1 truncate text-sm text-foreground">{children}</div>
    </div>
  );
}

function BookingDetailHeaderSlot({
  booking,
  contactName,
  scheduleSummary,
  jobNumber,
  onBack,
}: {
  booking: Booking;
  contactName?: string | null;
  scheduleSummary: string;
  jobNumber?: string | null;
  onBack: () => void;
}) {
  const contextHeader = (
    <div className="flex w-full min-w-0 items-start gap-3">
      <Button variant="ghost" size="icon-sm" className="shrink-0" onClick={onBack}>
        <ArrowLeft size={16} />
      </Button>
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2">
          <h2 className="truncate text-base font-semibold">
            {formatBookingTitle(booking, jobNumber)}
          </h2>
          <Badge
            variant={STATUS_VARIANT[booking.status] ?? 'outline'}
            className="hidden shrink-0 md:inline-flex"
          >
            {STATUS_LABEL[booking.status] ?? booking.status}
          </Badge>
        </div>
        <p className="mt-0.5 truncate text-xs text-muted-foreground">
          {[contactName, scheduleSummary].filter(Boolean).join(' · ')}
        </p>
      </div>
      {/* The backing Job owns billing, lineage, custom fields, and time entries;
          keep it one click away while this page retains request-only artifacts. */}
      {booking.jobId ? (
        <Button variant="outline" size="sm" className="shrink-0" asChild>
          <Link
            to="/dashboard/jobs/$id"
            params={{ id: booking.jobId }}
            search={{ tab: 'overview' as const }}
          >
            <Briefcase size={14} className="mr-1.5" />
            {jobNumber ? `Open job ${jobNumber}` : 'Open job'}
          </Link>
        </Button>
      ) : null}
    </div>
  );

  useWorkspaceHeader({ contextHeader }, [
    booking.id,
    booking.status,
    booking.serviceName,
    booking.jobId,
    jobNumber,
    contactName,
    scheduleSummary,
  ]);

  return null;
}

export function BookingDetailContent({
  bookingId,
  onBack,
  allowScheduling = false,
  onScheduled,
}: BookingDetailContentProps) {
  const router = useRouter();
  const { messageContact, callContact } = useContactComms();
  const [cancelOpen, setCancelOpen] = useState(false);
  const [startOpen, setStartOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  const [noteCreateSignal, setNoteCreateSignal] = useState(0);

  const query = useBooking(bookingId);
  // Display-only Company/My-time preference. Scheduler values remain UTC.
  const scheduleTz = useScheduleTimezone();
  // The linked-call panel reads the call-logs detail endpoint, which the backend
  // guards with `messages:view`. Service-call viewers don't necessarily have it,
  // so gate the whole section rather than mount a panel that can only fail.
  const canViewCalls = useHasPermission('messages:view');
  const pricingHidden = useIsFieldHidden('PRICING');
  const linkedJobQueryId = query.data?.jobId ?? '';
  const linkedAppointmentId = query.data?.appointmentId ?? '';
  const linkedJobQuery = useJob(linkedJobQueryId, { showErrorToast: false });
  const linkedAppointmentQuery = useJobAppointment(linkedJobQueryId, linkedAppointmentId);
  const cancelBooking = useCancelBooking();
  const updateStatus = useUpdateBookingStatus();
  // NOT-560 — create an estimate on the booking's backing SERVICE_CALL job.
  const createServiceCallEstimate = useCreateServiceCallEstimate(query.data?.jobId ?? '');

  const booking = query.data;

  useEffect(() => {
    if (query.data?.status === 'cancelled' || query.data?.status === 'completed') {
      setCancelOpen(false);
    }
  }, [query.data?.status]);

  if (query.isLoading) {
    return (
      <div className="mx-auto max-w-2xl space-y-4 px-5 py-5">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-32 w-full rounded-xl" />
        <Skeleton className="h-48 w-full rounded-xl" />
      </div>
    );
  }

  if (query.isError || !booking) {
    const is404 = query.error?.status === 404;
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3 text-muted-foreground">
        <AlertCircle size={32} />
        <p className="text-sm">{is404 ? 'Request not found' : 'Failed to load request'}</p>
        {!is404 && (
          <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
            <RefreshCw size={14} className="mr-1.5" />
            Retry
          </Button>
        )}
        <Button variant="ghost" size="sm" onClick={onBack}>
          Back to requests
        </Button>
      </div>
    );
  }

  const contactName = booking.contact
    ? `${booking.contact.firstName}${booking.contact.lastName ? ' ' + booking.contact.lastName : ''}`
    : null;

  const locked = isBookingLocked(booking.status);
  const canSchedule = booking.status === 'unscheduled' || allowScheduling;
  const canReschedule = !locked && booking.status !== 'unscheduled';
  const canAttachJob = !booking.jobId && booking.status !== 'cancelled';
  const appointmentId = booking.appointmentId;
  const linkedJobId = booking.jobId ?? null;
  const primaryTechName = booking.technicians?.[0] ? technicianName(booking.technicians[0]) : null;

  function handleStatusChange(status: BookingStatus) {
    updateStatus.mutate({ id: booking!.id, status });
  }

  function handleCancelBooking() {
    setCancelOpen(false);
    cancelBooking.mutate(booking!.id, {
      onSuccess: onBack,
    });
  }

  function handleAddNote() {
    setNoteCreateSignal((current) => current + 1);
  }

  function handleNextStepAction(action: BookingQuickAction) {
    switch (action) {
      case 'schedule':
      case 'reschedule':
        setScheduleOpen(true);
        break;
      case 'startWork':
        setStartOpen(true);
        break;
      case 'completeVisit':
        handleStatusChange('completed');
        break;
      case 'createEstimate':
        // NOT-560 — fan out a new estimate from the service call, then jump to it.
        if (!linkedJobId) return;
        createServiceCallEstimate.mutate(undefined, {
          onSuccess: (result) => {
            void router.navigate({
              to: '/dashboard/estimates/$id',
              params: { id: result.estimateId },
              search: DEFAULT_ESTIMATE_DETAIL_SEARCH,
            });
          },
        });
        break;
      case 'attachToJob':
        setAttachOpen(true);
        break;
      case 'viewVisit':
        if (!linkedJobId) return;
        if (!appointmentId) {
          void router.navigate({
            to: '/dashboard/jobs/$id',
            params: { id: linkedJobId },
            search: { tab: 'overview' },
          });
          return;
        }
        void router.navigate({
          to: '/dashboard/jobs/$id/appointments/$appointmentId',
          params: { id: linkedJobId, appointmentId },
        });
        break;
    }
  }

  const mapAddress = bookingAddressLabel(booking);
  const appointmentLike = linkedAppointmentQuery.data ?? bookingToAppointment(booking, mapAddress);
  const contactPhone = booking.contact?.phone;
  const contactEmail = booking.contact?.email;
  const contactId = booking.contactId ?? booking.contact?.id ?? null;
  const mapsUrl = mapAddress
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapAddress)}`
    : null;
  // Intake text captured on the booking itself (Appointment columns) — separate
  // from the Notes & Attachments timeline below, which is the shared Note record.
  const issueSummary = booking.issueSummary?.trim() || null;
  const customerNotes = booking.customerNotes?.trim() || null;
  const internalNotes = booking.internalNotes?.trim() || null;
  const issueSummaryText = issueSummary
    ? isHtmlContent(issueSummary)
      ? stripHtml(issueSummary)
      : issueSummary
    : null;
  const intakeText = issueSummaryText ?? customerNotes ?? internalNotes;
  // Collapsed preview — kept short so a long issue can't stretch the header row.
  const intakeSummary = intakeText
    ? intakeText.length > 48
      ? `${intakeText.slice(0, 48)}…`
      : intakeText
    : 'Nothing recorded';
  const dayBoundDate = dateStringToDate(booking.scheduledDate);
  const dayBoundLabel = booking.granularity === 'DAY_FLEXIBLE' ? 'All Day' : 'Full Day';
  // Internal schedule times render in the user's selected display zone. The
  // service-location zone is shown separately only when it differs.
  const displaySiteTz = locationZoneOf(booking.siteTimezone, booking.timezone);
  const scheduleSummary = booking.scheduledStart
    ? scheduleTz.format(booking.scheduledStart, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : dayBoundDate
      ? `${format(dayBoundDate, 'MMM d, yyyy')} · ${dayBoundLabel}`
      : 'Not scheduled';

  return (
    <>
      <BookingDetailHeaderSlot
        booking={booking}
        contactName={contactName}
        scheduleSummary={scheduleSummary}
        jobNumber={linkedJobQuery.data?.jobNumber ?? null}
        onBack={onBack}
      />
      <DetailPageLayout>
        <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
          <main className="min-w-0 space-y-4">
            <BookingNextStepCard
              booking={booking}
              isLocked={locked}
              allowScheduling={allowScheduling}
              techName={primaryTechName}
              scheduleSummary={scheduleSummary}
              isPending={updateStatus.isPending || cancelBooking.isPending}
              onAction={handleNextStepAction}
              onCancelVisit={() => setCancelOpen(true)}
            />

            {/* Iris handoff context (briefing, intake answers, conversation)
                recorded when Iris took this booking. */}
            <JobIrisSurveyCard job={booking} />

            <section className="rounded-lg border bg-background p-4 shadow-xs">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Customer
                  </p>
                  {booking.contact?.id ? (
                    <Link
                      to="/dashboard/contacts/$contactId"
                      params={{ contactId: booking.contact.id }}
                      className="mt-0.5 block truncate text-sm font-medium hover:underline"
                    >
                      {contactName ?? 'Unknown customer'}
                    </Link>
                  ) : (
                    <p className="mt-0.5 truncate text-sm font-medium">
                      {contactName ?? 'Unknown customer'}
                    </p>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-1.5">
                  {contactPhone ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 px-2 text-xs"
                      type="button"
                      // NOT-557 — route through the shared comms handler: places the
                      // call in-app when the softphone is registered (falling back to
                      // the OS dialer), dials a normalized E.164 number, and surfaces
                      // a visible error instead of a dead tel: link.
                      onClick={() =>
                        callContact({
                          id: contactId ?? '',
                          primaryPhone: contactPhone,
                          primaryEmail: contactEmail ?? null,
                        })
                      }
                    >
                      <Phone className="mr-1 size-3.5" />
                      Call
                    </Button>
                  ) : null}
                  {contactId ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 px-2 text-xs"
                      type="button"
                      onClick={() => void messageContact(contactId)}
                    >
                      <MessageSquare className="mr-1 size-3.5" />
                      Text
                    </Button>
                  ) : contactPhone ? (
                    <Button asChild variant="outline" size="sm" className="h-7 px-2 text-xs">
                      <a href={`sms:${contactPhone}`}>
                        <MessageSquare className="mr-1 size-3.5" />
                        Text
                      </a>
                    </Button>
                  ) : null}
                  {contactEmail ? (
                    <Button asChild variant="outline" size="sm" className="h-7 px-2 text-xs">
                      <a href={`mailto:${contactEmail}`}>
                        <Mail className="mr-1 size-3.5" />
                        Email
                      </a>
                    </Button>
                  ) : null}
                  {mapsUrl ? (
                    <Button asChild variant="outline" size="sm" className="h-7 px-2 text-xs">
                      <a href={mapsUrl} target="_blank" rel="noopener noreferrer">
                        <MapPin className="mr-1 size-3.5" />
                        Maps
                      </a>
                    </Button>
                  ) : null}
                </div>
              </div>
            </section>

            {/* What the caller reported and the two note lanes typed on intake.
                Without this the text was saved but never shown back anywhere on
                this page (the Notes & Attachments section below is a different
                record entirely). */}
            <CollapsibleSectionCard
              icon={FileText}
              title="Issue & notes"
              summary={intakeSummary}
              defaultOpen={Boolean(intakeText)}
            >
              <div className="space-y-3 p-4">
                <div className="min-w-0">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Issue summary
                  </p>
                  {issueSummary ? (
                    <RichTextContent
                      content={issueSummary}
                      className="mt-1 text-sm leading-6 text-foreground"
                    />
                  ) : (
                    <p className="mt-1 text-sm leading-6 text-muted-foreground">
                      No issue summary recorded.
                    </p>
                  )}
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="overflow-hidden rounded-lg border">
                    <div className="flex items-center justify-between gap-2 border-b bg-muted/40 px-3 py-2 text-xs font-semibold">
                      <span className="inline-flex items-center gap-1.5">
                        <MessageSquare className="size-3.5" />
                        Customer notes
                      </span>
                      <span className="font-medium text-brand-light-grey">from booking</span>
                    </div>
                    <p
                      className={cn(
                        'whitespace-pre-wrap p-3 text-sm leading-6',
                        customerNotes ? 'text-foreground' : 'text-muted-foreground',
                      )}
                    >
                      {customerNotes ?? 'No customer notes from booking.'}
                    </p>
                  </div>
                  <div className="overflow-hidden rounded-lg border">
                    <div className="flex items-center justify-between gap-2 border-b bg-muted/40 px-3 py-2 text-xs font-semibold">
                      <span className="inline-flex items-center gap-1.5">
                        <Lock className="size-3.5" />
                        Internal notes
                      </span>
                      <span className="font-medium text-brand-light-grey">team only</span>
                    </div>
                    <p
                      className={cn(
                        'whitespace-pre-wrap p-3 text-sm leading-6',
                        internalNotes ? 'text-foreground' : 'text-muted-foreground',
                      )}
                    >
                      {internalNotes ?? 'No internal notes.'}
                    </p>
                  </div>
                </div>
              </div>
            </CollapsibleSectionCard>

            <CollapsibleSectionCard
              icon={CalendarPlus}
              title="Schedule"
              summary={scheduleSummary}
              defaultOpen
            >
              <div className="grid gap-3 p-4 sm:grid-cols-2">
                <DetailField label="Job type">
                  {booking.jobType?.name ?? booking.serviceName ?? '-'}
                </DetailField>
                <DetailField label={booking.granularity === 'DAY_FLEXIBLE' ? 'Day' : 'Start'}>
                  {booking.scheduledStart
                    ? scheduleTz.format(booking.scheduledStart, {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                      })
                    : dayBoundDate
                      ? `${format(dayBoundDate, 'MMM d, yyyy')} · ${dayBoundLabel}`
                      : 'Not scheduled'}
                </DetailField>
                <DetailField label="End">
                  {booking.scheduledEnd
                    ? scheduleTz.format(booking.scheduledEnd, {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                      })
                    : booking.granularity === 'DAY_FLEXIBLE' && dayBoundDate
                      ? 'Route decides time'
                      : '-'}
                </DetailField>
                <DetailField label="Duration">
                  {booking.duration ? (
                    <span className="inline-flex items-center gap-1">
                      <Clock size={12} className="text-muted-foreground" />
                      {booking.duration} min
                    </span>
                  ) : (
                    '-'
                  )}
                </DetailField>
                {scheduleTz.hasDisplayChoice ? (
                  <DetailField label="Timezone">
                    <span className="inline-flex items-center gap-1.5">
                      {formatTimeZoneLabel(scheduleTz.zoneFor(booking.siteTimezone)) || '-'}
                      <span className="text-xs text-muted-foreground">
                        ({SCHEDULE_MODE_LABELS[scheduleTz.mode]})
                      </span>
                    </span>
                  </DetailField>
                ) : null}
                <div className="sm:col-span-2">
                  <ScheduleTimezoneToggle />
                </div>
              </div>
            </CollapsibleSectionCard>

            <CollapsibleSectionCard
              icon={MapPin}
              title="Service address"
              summary={mapAddress ?? 'No address'}
              defaultOpen={Boolean(mapAddress)}
            >
              <div className="space-y-3 p-4">
                {mapAddress ? (
                  <>
                    <p className="text-sm text-muted-foreground">{mapAddress}</p>
                    {scheduleTz.differsFromDisplay(displaySiteTz, booking.siteTimezone) ? (
                      <p className="text-xs text-muted-foreground">
                        Location time: {formatTimeZoneLabel(displaySiteTz)}
                      </p>
                    ) : null}
                    <Button variant="outline" size="sm" asChild>
                      <a href={mapsUrl ?? '#'} target="_blank" rel="noopener noreferrer">
                        <MapPin size={14} className="mr-1.5" />
                        Open in Maps
                      </a>
                    </Button>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">No service address recorded.</p>
                )}
              </div>
            </CollapsibleSectionCard>

            {/* NOT-560 parity — the service call's whole purpose is
                call → estimate(s) → converted job; that lineage and the
                customer's equipment live on the backing job and must be
                workable from here, not only from the jobs page. */}
            {linkedJobId ? (
              <JobRelationshipsCard jobId={linkedJobId} pricingHidden={pricingHidden} />
            ) : null}
            {linkedJobId ? (
              <JobEquipmentSection
                jobId={linkedJobId}
                customerId={booking.contactId ?? booking.contact?.id ?? null}
                serviceAddressId={linkedJobQuery.data?.serviceAddress?.addressId ?? null}
                canEdit={!locked}
              />
            ) : null}

            {/* NOT-558 — Notes and Attachments are one section: the note composer
                carries text + inline file attachments together, and any legacy
                booking-level attachments (uploaded without a note) still show below. */}
            <section className="overflow-hidden rounded-lg border bg-background shadow-xs">
              <header className="flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
                <div className="flex min-w-0 items-center gap-2">
                  <StickyNote className="size-4 shrink-0 text-muted-foreground" />
                  <h3 className="truncate text-base font-semibold text-foreground">
                    Notes &amp; Attachments
                  </h3>
                  {booking.attachments?.length ? (
                    <span className="text-sm font-medium text-muted-foreground">
                      {booking.attachments.length} file
                      {booking.attachments.length === 1 ? '' : 's'}
                    </span>
                  ) : null}
                </div>
                <Button variant="outline" size="sm" onClick={handleAddNote}>
                  <StickyNote size={14} className="mr-1.5" />
                  Add note
                </Button>
              </header>
              <div className="space-y-4 p-4">
                <EntityNotes
                  entityType="BOOKING"
                  entityId={bookingId}
                  heading="Notes"
                  description="Internal request notes for dispatch and office follow-up. Attach files to a note to keep photos and paperwork with their context."
                  emptyText={
                    booking.attachments?.length ? 'No notes yet.' : 'No notes or attachments yet.'
                  }
                  defaultVisibility="INTERNAL"
                  framed
                  icon={StickyNote}
                  createSignal={noteCreateSignal}
                  cardClickToEdit
                  hideHeader
                />
                {booking.attachments?.length ? (
                  <div className="space-y-2 border-t pt-4">
                    <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      <Paperclip size={12} />
                      Attachments
                    </p>
                    {booking.attachments.map((att) => (
                      <a
                        key={att.id}
                        href={att.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 rounded-md border p-3 text-sm hover:bg-muted/50"
                      >
                        <Paperclip size={12} className="text-muted-foreground" />
                        {att.name}
                        {att.size ? (
                          <span className="ml-auto text-xs text-muted-foreground">
                            {(att.size / 1024).toFixed(0)} KB
                          </span>
                        ) : null}
                      </a>
                    ))}
                  </div>
                ) : null}
              </div>
            </section>

            {appointmentId ? (
              <>
                <CollapsibleSectionCard
                  icon={CheckSquare}
                  title="Checklists"
                  summary="Technician workflow"
                >
                  <div className="p-4">
                    <AppointmentChecklists
                      appointmentId={appointmentId}
                      locked={locked}
                      lockedReason={lockedReasonForBooking(booking.status)}
                      showHeader={false}
                    />
                  </div>
                </CollapsibleSectionCard>

                <CollapsibleSectionCard
                  icon={FileSignature}
                  title="Customer forms"
                  summary="Customer documentation"
                >
                  <div className="px-4 pb-1 pt-3 text-xs text-muted-foreground">
                    Customer-signed documentation for this request.
                  </div>
                  <div className="p-4 pt-2">
                    <AppointmentFormSubmissions appointmentId={appointmentId} readOnly={locked} />
                  </div>
                </CollapsibleSectionCard>
              </>
            ) : null}

            {/* NOT-15 — the phone call this service call was created from. We only
                store its call-control id (sourceCallControlId), so BookingLinkedCall
                fetches the full record on demand and reuses the shared call-detail
                body (recording, transcript, AI insight, routing, quality). Read-only
                here; creating/attaching the link comes in a later slice. */}
            {booking.sourceCallControlId && canViewCalls ? (
              <CollapsibleSectionCard
                icon={PhoneCall}
                title="Linked call"
                summary="Recording, transcript & AI insight"
                defaultOpen
              >
                <BookingLinkedCall callControlId={booking.sourceCallControlId} />
              </CollapsibleSectionCard>
            ) : null}

            {(booking.quoteId || booking.invoiceId) && (
              <CollapsibleSectionCard
                icon={FileText}
                title="Linked documents"
                count={[booking.quoteId, booking.invoiceId].filter(Boolean).length}
                summary="Quote and invoice"
              >
                <div className="space-y-2 p-4">
                  {booking.quoteId ? (
                    <Link
                      to="/dashboard/estimates/$id"
                      params={{ id: booking.quoteId }}
                      search={DEFAULT_ESTIMATE_DETAIL_SEARCH}
                      className="flex items-center gap-2 text-sm text-primary hover:underline"
                    >
                      <FileText size={12} />
                      View quote
                    </Link>
                  ) : null}
                  {booking.invoiceId ? (
                    <Link
                      to="/dashboard/invoices/$id"
                      params={{ id: booking.invoiceId }}
                      search={{ ...DEFAULT_INVOICE_LIST_FILTERS, tab: 'overview' }}
                      className="flex items-center gap-2 text-sm text-primary hover:underline"
                    >
                      <DollarSign size={12} />
                      View invoice
                    </Link>
                  ) : null}
                </div>
              </CollapsibleSectionCard>
            )}
          </main>

          <VisitAside
            appointment={appointmentLike}
            job={appointmentId ? linkedJobQuery.data : undefined}
          />
        </div>

        {startOpen && (
          <BookingStartVisitDialog
            jobId={linkedJobId ?? ''}
            appointmentId={appointmentId ?? booking.id}
            onClose={() => setStartOpen(false)}
          />
        )}
        {/* ── Cancel dialog ──────────────────────────────────────────────── */}
        <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Cancel visit</DialogTitle>
              <DialogDescription>
                Are you sure you want to cancel this visit? It will remain in history.
                {booking.invoiceId && !booking.invoicePaid && (
                  <span className="block mt-2 font-medium text-destructive">
                    Warning: This visit has an unpaid invoice. Cancelling it may affect billing.
                  </span>
                )}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCancelOpen(false)}>
                Keep visit
              </Button>
              <Button
                variant="destructive"
                onClick={handleCancelBooking}
                disabled={cancelBooking.isPending}
              >
                {cancelBooking.isPending && <Loader2 size={14} className="mr-1.5 animate-spin" />}
                Cancel visit
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {canAttachJob && (
          <AttachJobDialog booking={booking} open={attachOpen} onOpenChange={setAttachOpen} />
        )}

        {(canSchedule || canReschedule) && (
          <ScheduleBookingDialog
            bookingId={bookingId}
            serviceTypeId={booking.serviceType?.id}
            initialDurationMinutes={booking.duration ?? null}
            open={scheduleOpen}
            onOpenChange={setScheduleOpen}
            onScheduled={onScheduled}
          />
        )}
      </DetailPageLayout>
    </>
  );
}
